import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.thumbnailUrl}
            alt={article.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">{article.date} · {article.readTime} 分钟阅读</p>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block"
      >
        <div className="relative overflow-hidden rounded-lg aspect-[16/10] mb-4">
          <img
            src={article.imageUrl}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <span className="inline-block text-xs text-white/80 bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full mb-2 whitespace-nowrap">
              {article.tags[0]}
            </span>
            <h3 className="text-lg md:text-xl font-heading font-semibold text-white leading-snug line-clamp-2">
              {article.title}
            </h3>
          </div>
        </div>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center gap-3 mt-3">
          <img src={article.authorAvatar} alt={article.author} className="w-6 h-6 rounded-full object-cover" />
          <span className="text-xs text-foreground-500 whitespace-nowrap">{article.author} · {article.date}</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative overflow-hidden aspect-[16/10]">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3">
          <span className="inline-block text-xs text-background-50 bg-primary-500/90 backdrop-blur-sm px-2.5 py-1 rounded-full whitespace-nowrap">
            {article.tags[0]}
          </span>
        </div>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 mt-2 leading-relaxed line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-background-200/60">
          <div className="flex items-center gap-2">
            <img src={article.authorAvatar} alt={article.author} className="w-6 h-6 rounded-full object-cover" />
            <span className="text-xs text-foreground-500 whitespace-nowrap">{article.author}</span>
          </div>
          <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date} · {article.readTime}分钟</span>
        </div>
      </div>
    </Link>
  );
}